const toNumber = (value) => {
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
};

const calculateWholePercentages = (ingredientGrams, totalIngredientGrams) => {
	const exactPercentages = ingredientGrams.map((grams) => (grams / totalIngredientGrams) * 100);
	const percentages = exactPercentages.map(Math.floor);
	const percentagePointsToDistribute = 100 - percentages.reduce((sum, percentage) => sum + percentage, 0);

	const indexesByLargestRemainder = exactPercentages
		.map((percentage, index) => ({ index, remainder: percentage - percentages[index] }))
		.sort((first, second) => second.remainder - first.remainder || first.index - second.index);

	for (let index = 0; index < percentagePointsToDistribute; index++) {
		percentages[indexesByLargestRemainder[index].index] += 1;
	}

	return percentages;
};

/**
 * Calculate the ingredient and water weights for one machine cup size.
 */
export const calculateCupAmounts = (smoothieParts, cupSize, waterRatio, weightRatio) => {
	const numericCupSize = toNumber(cupSize);
	const numericWaterRatio = toNumber(waterRatio);
	const numericWeightRatio = toNumber(weightRatio);
	const percentages = smoothieParts.map((part) => toNumber(part.percentage));

	if (
		numericCupSize === null ||
		numericWaterRatio === null ||
		numericWeightRatio === null ||
		percentages.some((percentage) => percentage === null)
	) {
		return null;
	}

	const totalIngredientPercentage = percentages.reduce((sum, percentage) => sum + percentage, 0);
	const totalWaterPercentage = percentages.reduce(
		(sum, percentage) => sum + percentage * numericWaterRatio,
		0,
	);
	const totalPercentage = totalIngredientPercentage + totalWaterPercentage;

	if (totalPercentage === 0) {
		return null;
	}

	const allowedCupWeight = numericCupSize * numericWeightRatio;
	const cupWeightRatio = allowedCupWeight / totalPercentage;
	const ingredientGrams = percentages.map((percentage) => percentage * cupWeightRatio);
	const totalIngredientGrams = ingredientGrams.reduce((sum, grams) => sum + grams, 0);
	const amountOfWater = totalIngredientGrams * numericWaterRatio;

	return {
		ingredientGrams,
		totalIngredientGrams,
		amountOfWater,
	};
};

/**
 * Derive the recipe percentages and ratios from the gram amounts for one cup size.
 */
export const calculateRecipeFromCupAmounts = (ingredientGrams, amountOfWater, cupSize) => {
	const numericIngredientGrams = ingredientGrams.map(toNumber);
	const numericAmountOfWater = toNumber(amountOfWater);
	const numericCupSize = toNumber(cupSize);

	if (
		numericIngredientGrams.length === 0 ||
		numericIngredientGrams.some((grams) => grams === null || grams < 0) ||
		numericAmountOfWater === null ||
		numericAmountOfWater < 0 ||
		numericCupSize === null ||
		numericCupSize <= 0
	) {
		return null;
	}

	const totalIngredientGrams = numericIngredientGrams.reduce((sum, grams) => sum + grams, 0);
	if (totalIngredientGrams === 0) {
		return null;
	}

	const percentages = calculateWholePercentages(numericIngredientGrams, totalIngredientGrams);

	return {
		percentages,
		waterRatio: numericAmountOfWater / totalIngredientGrams,
		weightRatio: (totalIngredientGrams + numericAmountOfWater) / numericCupSize,
	};
};
